import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import FeatureHelp from '../help/FeatureHelp';
import NotificationCenter from './NotificationCenter';
import {
  LayoutDashboard, FileText, ClipboardList, Truck, Users, Briefcase,
  Calendar, CalendarDays, Bot, Settings, LogOut, ChevronRight, ChevronDown, Menu, X,
  BarChart3, Receipt, FileSignature, HelpCircle, Building2, Package, Boxes, Landmark, TrendingUp, Calculator, Upload, Link as LinkIcon, HandCoins, Repeat, FolderKanban, Sparkles,
  ShoppingCart, Wallet, BookOpen, Wrench, Palette, Activity, Inbox, Layers, ShieldCheck, Archive
} from 'lucide-react';

// ===== Grouped navigation =====
// Each group is rendered as a card with a soft shadow that visually separates
// it from the others. Items inside use a neutral style; the active item uses
// the brand orange.
const dashboardItem = { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' };

const navGroups = [
  {
    key: 'sales',
    label: 'Ventas',
    icon: ShoppingCart,
    color: 'text-orange-600',
    items: [
      { path: '/invoices', icon: FileText, label: 'Facturas' },
      { path: '/proformas', icon: Layers, label: 'Proformas' },
      { path: '/subscriptions', icon: Repeat, label: 'Suscripciones' },
      { path: '/quotes', icon: ClipboardList, label: 'Presupuestos' },
      { path: '/delivery-notes', icon: Truck, label: 'Albaranes' },
      { path: '/contracts', icon: FileSignature, label: 'Contratos' },
      { path: '/receivables', icon: HandCoins, label: 'Por Cobrar' },
    ],
  },
  {
    key: 'purchases',
    label: 'Compras',
    icon: Receipt,
    color: 'text-rose-600',
    items: [
      { path: '/expenses', icon: Receipt, label: 'Gastos' },
      { path: '/expenses/inbox', icon: Upload, label: 'Bandeja OCR', badgeKey: 'ocrInbox' },
      { path: '/providers', icon: Package, label: 'Proveedores' },
    ],
  },
  {
    key: 'catalog',
    label: 'Catálogo',
    icon: BookOpen,
    color: 'text-sky-600',
    items: [
      { path: '/clients', icon: Users, label: 'Clientes', badgeKey: 'portalUnread' },
      { path: '/services', icon: Briefcase, label: 'Servicios' },
      { path: '/articles', icon: Boxes, label: 'Artículos' },
    ],
  },
  {
    key: 'operations',
    label: 'Operaciones',
    icon: FolderKanban,
    color: 'text-indigo-600',
    items: [
      { path: '/projects', icon: FolderKanban, label: 'Proyectos' },
      { path: '/calendar', icon: CalendarDays, label: 'Calendario' },
    ],
  },
  {
    key: 'finance',
    label: 'Finanzas',
    icon: Wallet,
    color: 'text-emerald-600',
    items: [
      { path: '/banking', icon: Landmark, label: 'Banca' },
      { path: '/banking-psd2', icon: LinkIcon, label: 'Banca PSD2' },
      { path: '/cashflow', icon: TrendingUp, label: 'Cash Flow' },
      { path: '/tax', icon: Calculator, label: 'Impuestos' },
      { path: '/tax/forecast', icon: Sparkles, label: 'Predicción Fiscal' },
      { path: '/inspeccion', icon: ShieldCheck, label: 'Agente Inspección' },
    ],
  },
  {
    key: 'analytics',
    label: 'Análisis',
    icon: BarChart3,
    color: 'text-violet-600',
    items: [
      { path: '/reports', icon: BarChart3, label: 'Informes' },
      { path: '/export-center', icon: Archive, label: 'Centro Exportación' },
      { path: '/history', icon: Calendar, label: 'Historial' },
    ],
  },
  {
    key: 'tools',
    label: 'Herramientas',
    icon: Wrench,
    color: 'text-amber-600',
    items: [
      { path: '/import', icon: Upload, label: 'Importar datos' },
      { path: '/templates', icon: Palette, label: 'Plantillas factura' },
      { path: '/help', icon: HelpCircle, label: 'Ayuda' },
    ],
  },
];

const adminItems = [
  { path: '/admin/companies', icon: Building2, label: 'Empresas' },
  { path: '/admin/analytics', icon: Activity, label: 'Analítica web' },
  { path: '/admin/demo-requests', icon: Inbox, label: 'Solicitudes Demo', badgeKey: 'demoRequests' },
];

const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cfAlerts, setCfAlerts] = useState(0);
  const [ocrPending, setOcrPending] = useState(0);
  const [portalUnread, setPortalUnread] = useState(0);
  const [demoNew, setDemoNew] = useState(0);
  const navScrollRef = useRef(null);

  // Collapsed groups state, persisted in sessionStorage
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = sessionStorage.getItem('sidebarCollapsedGroups');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Auto-open the group that contains the active route (so users never lose context).
  useEffect(() => {
    const activeGroup = navGroups.find(g =>
      g.items.some(it => location.pathname === it.path || location.pathname.startsWith(it.path + '/'))
    );
    if (activeGroup && collapsedGroups[activeGroup.key]) {
      const next = { ...collapsedGroups, [activeGroup.key]: false };
      setCollapsedGroups(next);
      sessionStorage.setItem('sidebarCollapsedGroups', JSON.stringify(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      sessionStorage.setItem('sidebarCollapsedGroups', JSON.stringify(next));
      return next;
    });
  };

  // Restore sidebar scroll synchronously after every navigation (before paint)
  React.useLayoutEffect(() => {
    const saved = parseInt(sessionStorage.getItem('sidebarScroll') || '0', 10);
    if (navScrollRef.current && saved > 0) {
      navScrollRef.current.scrollTop = saved;
    }
  });

  // Save sidebar scroll on each scroll event. Re-attach the listener whenever
  // the route changes, because NavContent is recreated and the <nav> element
  // is a new DOM node each navigation.
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        sessionStorage.setItem('sidebarScroll', String(el.scrollTop));
        raf = null;
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [location.pathname]);

  useEffect(() => {
    api.get('/cashflow/projection', { params: { months: 6 } })
      .then(res => setCfAlerts(res.data?.alerts?.length || 0))
      .catch(() => {});
    api.get('/expense-uploads', { params: { limit: 1 } })
      .then(res => {
        const c = res.data?.counts || {};
        setOcrPending((c.uploaded || 0) + (c.processing || 0) + (c.extracted || 0) + (c.failed || 0));
      })
      .catch(() => {});
    api.get('/portal/admin/notifications', { params: { unread_only: true } })
      .then(res => setPortalUnread(res.data?.unread || 0))
      .catch(() => {});
    if (user?.role === 'admin') {
      api.get('/admin/demo-requests/stats')
        .then(res => setDemoNew(res.data?.pending || 0))
        .catch(() => {});
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.path ||
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/')) ||
      (item.path !== '/dashboard' && location.pathname === item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={closeMobileMenu}
        className={`sidebar-link ${isActive ? 'active' : ''}`}
        data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
      >
        <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
        <span>{item.label}</span>
        {item.path === '/cashflow' && cfAlerts > 0 && (
          <span className="ml-auto w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{cfAlerts}</span>
        )}
        {item.badgeKey === 'ocrInbox' && ocrPending > 0 && (
          <span className="ml-auto px-1.5 min-w-[20px] h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="ocr-inbox-badge">{ocrPending}</span>
        )}
        {item.badgeKey === 'portalUnread' && portalUnread > 0 && (
          <span className="ml-auto px-1.5 min-w-[20px] h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="portal-unread-badge">{portalUnread}</span>
        )}
        {item.badgeKey === 'demoRequests' && demoNew > 0 && (
          <span className="ml-auto px-1.5 min-w-[20px] h-5 bg-[#FF6B4A] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="demo-requests-badge">{demoNew}</span>
        )}
      </Link>
    );
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <nav ref={navScrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {/* Dashboard standalone */}
        {renderNavItem(dashboardItem)}

        {/* Grouped navigation — each group is a card with subtle shadow */}
        {navGroups.map((group) => {
          const collapsed = !!collapsedGroups[group.key];
          const hasActive = group.items.some(it =>
            location.pathname === it.path || location.pathname.startsWith(it.path + '/')
          );
          return (
            <div
              key={group.key}
              className={`mt-3 bg-white rounded-2xl border border-stone-100/80 transition-all duration-200 ${
                hasActive
                  ? 'shadow-[0_4px_16px_rgba(255,107,74,0.08)] ring-1 ring-orange-100/60'
                  : 'shadow-[0_2px_6px_rgba(0,0,0,0.03)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.05)]'
              }`}
            >
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-2xl transition-colors ${
                  hasActive ? 'text-stone-800' : 'hover:text-stone-800'
                }`}
                data-testid={`nav-group-${group.key}`}
              >
                <group.icon className={`w-3.5 h-3.5 ${group.color || 'text-stone-500'}`} strokeWidth={2.2} />
                <span className={`text-[10px] uppercase tracking-[0.15em] font-bold flex-1 text-left ${group.color || 'text-stone-500'}`}>
                  {group.label}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 text-stone-400 ${collapsed ? '-rotate-90' : ''}`}
                  strokeWidth={2}
                />
              </button>
              {!collapsed && (
                <div className="px-1.5 pb-1.5 space-y-0.5 animate-fade-in-up">
                  {group.items.map(renderNavItem)}
                </div>
              )}
            </div>
          );
        })}

        {user?.role === 'admin' && (
          <div className="pt-4">
            <div className="px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-400">Admin</span>
            </div>
            <div className="mt-1 space-y-0.5">
              {adminItems.map(renderNavItem)}
            </div>
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 px-3 pb-2 space-y-2">
        <Link
          to="/auxiliar"
          onClick={closeMobileMenu}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
            location.pathname === '/auxiliar' 
              ? 'bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white shadow-lg shadow-orange-200'
              : 'bg-orange-50 text-[#FF6B4A] hover:bg-orange-100'
          }`}
          data-testid="nav-auxiliar"
        >
          <Bot className="w-[18px] h-[18px]" strokeWidth={1.8} />
          <span className="font-semibold text-sm">Auxiliar IA</span>
          <ChevronRight className="w-4 h-4 ml-auto" />
        </Link>
        <Link
          to="/ai-advisor"
          onClick={closeMobileMenu}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
            location.pathname === '/ai-advisor'
              ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-lg shadow-violet-200'
              : 'bg-violet-50 text-violet-700 hover:bg-violet-100'
          }`}
          data-testid="nav-ai-advisor"
        >
          <Sparkles className="w-[18px] h-[18px]" strokeWidth={1.8} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight">Asesor IA</p>
            <p className="text-[10px] opacity-75 leading-tight">Análisis con Claude</p>
          </div>
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="flex-shrink-0 p-3 border-t border-stone-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-stone-800 truncate">{user?.name}</p>
              <p className="text-[11px] text-stone-400 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <NotificationCenter />
            <Link
              to="/settings"
              onClick={closeMobileMenu}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              title="Configuración"
              data-testid="settings-button"
            >
              <Settings className="w-4 h-4 text-stone-400" />
            </Link>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              title="Cerrar sesión"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 text-stone-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/80 backdrop-blur-xl border-b border-stone-100 z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 hover:bg-stone-100 rounded-xl" data-testid="mobile-menu-button">
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
          <div className="w-7 h-7 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-lg flex items-center justify-center">
            <FileText className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <span className="text-lg font-heading font-extrabold tracking-tight text-stone-900">Facturcal</span>
        </Link>
        <div className="w-10" />
      </header>

      {mobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/10 z-40 backdrop-blur-sm" onClick={closeMobileMenu} />}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed top-14 left-0 h-[calc(100vh-3.5rem)] w-64 bg-white/90 backdrop-blur-xl border-r border-stone-100 z-50 transform transition-transform duration-300 ease-in-out overflow-hidden ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <NavContent />
      </aside>

      {/* Desktop Floating Sidebar */}
      <aside className="hidden lg:block fixed left-4 top-4 h-[calc(100vh-2rem)] w-56 bg-white/80 backdrop-blur-xl rounded-3xl border border-stone-200/60 z-40 overflow-hidden shadow-sm">
        <div className="h-16 flex items-center px-5 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="logo-link">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-lg flex items-center justify-center shadow-sm">
              <FileText className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="text-lg font-heading font-extrabold tracking-tight text-stone-900">Facturcal</span>
          </Link>
        </div>
        <div className="h-[calc(100%-4rem)]">
          <NavContent />
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-5 md:p-8 max-w-[1400px]">
          {children}
        </div>
      </main>

      {/* Contextual help button (top-right) */}
      <FeatureHelp />
    </div>
  );
};

export default MainLayout;
